import { Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, useToast } from '@chakra-ui/react';
import { FC, useState } from 'react';
import { FiCopy, FiLink } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { getScorLocale, scorT } from 'i18n/scorLocale';
import AppRoute from 'router/AppRoute';
import { normalizeScorLineBreaks } from './scorText';
import PdfViewer, { preloadScorPdfViewer } from './ScorPdfViewer';
import styles from './scor.module.scss';

const preparePdfViewer = (): void => { void preloadScorPdfViewer().catch(() => {}); };

export const ScorPagination: FC<{ page: number; pagination?: ApiScorPagination; onPage: (page: number) => void }> = ({ page, pagination, onPage }) => {
  const total = pagination?.total_pages ?? 1;
  if (total <= 1) return null;

  return <nav className={styles.pagination} aria-label={scorT('pageOf', { page, total })}>
    <Button variant="outline" isDisabled={page <= 1} onClick={() => onPage(page - 1)}>{scorT('previousPage')}</Button>
    <span>{scorT('pageOf', { page, total })}</span>
    <Button variant="outline" isDisabled={page >= total} onClick={() => onPage(page + 1)}>{scorT('nextPage')}</Button>
  </nav>;
};

export const ScorCopy: FC<{ value: string; label: string; hideValue?: boolean; appearance?: 'inviteLink' | 'inviteCode' }> = ({ value, label, hideValue = false, appearance }) => {
  const toast = useToast();
  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: scorT('copiedValue'), status: 'success', position: 'top-right', duration: 2500, isClosable: true });
    } catch {
      toast({ title: scorT('copyFailed'), status: 'error', position: 'top-right', duration: 4000, isClosable: true });
    }
  };

  return <div className={`${styles.copyValue} ${appearance ? styles[appearance] : ''}`}>
    {!hideValue && <span>{value}</span>}
    <Button size="sm" variant={appearance ? 'ghost' : 'outline'} aria-label={label} title={appearance === 'inviteCode' ? label : undefined} onClick={() => { void copy(); }}>
      {appearance === 'inviteCode' ? <FiCopy aria-hidden="true" /> : <>{appearance === 'inviteLink' && <FiLink aria-hidden="true" />}{label}</>}
    </Button>
  </div>;
};

type Report = ApiScorMyReportDetail | ApiScorPoolReportDetail;

export const ScorReportSections: FC<{ report: Report }> = ({ report }) => <>
    {(report.sections ?? []).map((section, index) => <section className={`${styles.card} ${styles.reportSection}`} key={`${section.code}-${index}`}>
      <h2 className={styles.sectionTitle}>{section.title}</h2>
      <div className={styles.multilineText}>{normalizeScorLineBreaks(section.text)}</div>
    </section>)}
</>;

export const ScorReportHistory: FC<{ report: Report }> = ({ report }) => <>
    {Boolean(report.history?.length) && <section className={`${styles.card} ${styles.reportSection}`}>
      <h2 className={styles.sectionTitle}>{scorT('productHistory')}</h2>
      <div className={styles.stack}>{report.history?.map((item) => <Link className={styles.historyRow} key={item.id} to={AppRoute.scorPoolDetail(item.id)}>
        <strong>{item.issuer?.trim() ? item.issuer.trim() : (item.public_code === '' ? item.id : item.public_code ?? item.id)}</strong>
        <span>{item.published_at ? new Date(item.published_at).toLocaleDateString(getScorLocale()) : '—'}</span>
        <span>SCOR {item.score ?? '—'}</span>
        <span>{item.outcome?.label}</span><span>{item.outcome?.actual}</span>
      </Link>)}</div>
    </section>}
</>;

export const ScorReportFiles: FC<{ report: Report }> = ({ report }) => {
  const [selected, setSelected] = useState<ApiScorReportFile>();
  // Foreign reports must never expose author files.
  const files = (report.files ?? []).filter((file) => report.is_mine === true || file.kind === 2);

  return <>
    <section className={styles.card}>
      <h2 className={styles.sectionTitle}>{scorT('reportFiles')}</h2>
      {files.length === 0 ? <p>{scorT('noReportFile')}</p> : files.map((file) => <div className={styles.reportFileRow} key={file.id}>
        <div><strong>{file.name}</strong><small>{new Intl.NumberFormat(getScorLocale(), { style: 'unit', unit: 'megabyte', maximumFractionDigits: 2 }).format(file.size / 1048576)}</small></div>
        <Button variant="outline" onPointerEnter={preparePdfViewer} onFocus={preparePdfViewer} onClick={() => setSelected(file)}>{scorT('openDocument')}</Button>
      </div>)}
    </section>
    <Modal isOpen={Boolean(selected)} onClose={() => setSelected(undefined)} size="4xl" scrollBehavior="inside">
      <ModalOverlay /><ModalContent h="calc(100dvh - 48px)" maxH="calc(100dvh - 48px)" my="24px"><ModalHeader flex="0 0 auto" pr="48px" overflowWrap="anywhere" maxH="25%" overflowY="auto">{selected?.name}</ModalHeader><ModalCloseButton />
        <ModalBody display="flex" flexDirection="column" minH={0} overflow="hidden" px={{ base: 2, md: 5 }} pb="16px">{selected && <PdfViewer key={selected.url} file={selected} />}</ModalBody>
      </ModalContent>
    </Modal>
  </>;
};
